import { ApiErrorSchema, type ApiError as ApiErrorBody } from "@scrile/contracts";
import { t, type MessageKey } from "../i18n";

/**
 * Ошибки API в одном месте. Сервер по контракту отвечает { error: код, message }; экраны
 * получают уже человеческий текст из словаря — на языке интерфейса, а не на языке
 * сервера, и одинаковый для одинаковых ситуаций.
 */

/**
 * Коды контракта плюс три своих: нет сети (fetch не дошёл), сервис недоступен (ответил
 * прокси или балансировщик, а не API) и ответ не по контракту.
 */
export type ApiErrorCode = ApiErrorBody["error"] | "network" | "unavailable" | "bad_response";

/**
 * Где случилась ошибка. Один код значит разное: «unauthorized» при входе — неверный
 * пароль, в плеере — «войдите»; «conflict» при регистрации — почта уже занята.
 */
export type ErrorContext = "default" | "login" | "register" | "checkout" | "episode" | "tip";

export type ApiIssue = NonNullable<ApiErrorBody["issues"]>[number];

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /** Текст сервера как есть: для консоли и отладки, не для экрана. */
  readonly serverMessage: string | null;
  /** У validation — какие поля не прошли; форма подсвечивает их своими словами. */
  readonly issues: readonly ApiIssue[];
  /** У 429 — через сколько секунд можно снова (Retry-After); null — сервер не сказал. */
  retryAfterSec: number | null = null;

  constructor(code: ApiErrorCode, status: number, serverMessage: string | null = null, issues: readonly ApiIssue[] = []) {
    super(errorText(code));
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.serverMessage = serverMessage;
    this.issues = issues;
  }

  describe(context: ErrorContext = "default"): string {
    return errorText(this.code, context);
  }
}

const BY_STATUS: Readonly<Record<number, ApiErrorCode>> = {
  400: "validation",
  413: "validation",
  401: "unauthorized",
  402: "payment_required",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  422: "validation",
  429: "rate_limited",
};

/**
 * Код по HTTP-статусу — когда тело не по контракту (HTML прокси, пустота). Настоящий
 * API на свои 5xx отвечает { error: "internal" }; 5xx без такого тела — это прокси
 * dev-сервера (500 при выключенном API) или балансировщик (502/503/504): до сервера
 * не достучались, и «сломалось у нас» было бы неправдой.
 */
export function codeFromStatus(status: number): ApiErrorCode {
  if (status === 0) return "network";
  if (status >= 500) return "unavailable";
  return BY_STATUS[status] ?? "internal";
}

/** Ответ с ошибкой → ApiError. Тело по контракту главнее статуса. */
export function errorFromResponse(status: number, body: unknown): ApiError {
  const parsed = ApiErrorSchema.safeParse(body);
  if (parsed.success) return new ApiError(parsed.data.error, status, parsed.data.message, parsed.data.issues ?? []);
  return new ApiError(codeFromStatus(status), status, null);
}

const DEFAULT_TEXTS: Readonly<Record<ApiErrorCode, MessageKey>> = {
  validation: "error.validation",
  not_found: "error.not_found",
  unauthorized: "error.unauthorized",
  forbidden: "error.forbidden",
  payment_required: "error.payment_required",
  conflict: "error.conflict",
  rate_limited: "error.rate_limited",
  internal: "error.internal",
  network: "error.network",
  unavailable: "error.unavailable",
  bad_response: "error.bad_response",
};

const CONTEXT_TEXTS: Readonly<Partial<Record<ErrorContext, Partial<Record<ApiErrorCode, MessageKey>>>>> = {
  login: { unauthorized: "error.login.unauthorized" },
  register: { conflict: "error.register.conflict" },
  checkout: { conflict: "error.checkout.conflict", unauthorized: "error.checkout.unauthorized" },
  episode: { payment_required: "error.episode.payment_required", not_found: "error.episode.not_found" },
  tip: { validation: "error.tip.validation" },
};

export function errorText(code: ApiErrorCode, context: ErrorContext = "default"): string {
  return t(CONTEXT_TEXTS[context]?.[code] ?? DEFAULT_TEXTS[code]);
}

/** Любая ошибка → текст для человека; не-API ошибки не показываются сырыми. */
export function describeError(error: unknown, context: ErrorContext = "default"): string {
  if (error instanceof ApiError) return error.describe(context);
  return t("error.unknown");
}

/** Retry-After в секундах (целое) или датой HTTP; кривое — null. */
export function parseRetryAfter(value: string | null, now: number = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d{1,6}$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - now) / 1000));
}

export function isApiError(error: unknown, code?: ApiErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}
